import { useState, useEffect, useCallback } from 'react';
import {
    listVideoContent,
    registerVideoContent,
    updateVideoContent,
    deleteVideoContent
} from './videoContentService';
import { useAzureUpload } from '../../utils/useAzureUpload';

const PAGE_SIZE_VIDEOS = 10;

export function useVideoLibrary() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // playerVideo stores { blobName, title } of the currently open video (null = closed)
    const [playerVideo, setPlayerVideo] = useState(null);

    const [editingVideo, setEditingVideo] = useState(null);

    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [actionError, setActionError] = useState('');

    const { uploadPhase, uploadProgress, uploadError, clearUploadError, executeUpload } = useAzureUpload();

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchVideos = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await listVideoContent(debouncedSearch, page, PAGE_SIZE_VIDEOS);
            setVideos(data.results || []);
            setTotalCount(data.count || 0);
        } catch (err) {
            setError('Failed to load videos. Please try again.');
            console.error('Error fetching videos:', err);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, page]);

    // Reset to page 1 whenever the search term changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchVideos();
    }, [fetchVideos]);

    const handlePageChange = useCallback((newPage) => {
        setPage(newPage);
    }, []);

    const handleSearch = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleOpenPlayer = useCallback((video) => {
        setPlayerVideo({ blobName: video.blobName, title: video.title });
    }, []);

    const handleClosePlayer = useCallback(() => {
        setPlayerVideo(null);
    }, []);

    const handleOpenEdit = useCallback((video) => {
        setEditingVideo(video);
    }, []);

    const handleCloseEdit = useCallback(() => {
        setEditingVideo(null);
    }, []);

    const handleOpenUploadModal = useCallback(() => {
        clearUploadError();
        setUploadSuccess('');
        setUploadModalOpen(true);
    }, [clearUploadError]);

    const handleCloseUploadModal = useCallback(() => {
        setUploadModalOpen(false);
        clearUploadError();
    }, [clearUploadError]);

    const handleUpload = useCallback(async ({ file, title, description, tags }) => {
        try {
            await executeUpload({
                file,
                prefix: 'icclibrary/videos/',
                thumbPrefix: 'icclibrary/thumbnails/',
                onRegister: async ({ blobName, thumbnailBlobName }) => {
                    await registerVideoContent({
                        blobName,
                        thumbnailBlobName: thumbnailBlobName ?? null,
                        originalFilename: file.name,
                        fileSizeBytes: file.size,
                        title,
                        ...(description && { description }),
                        ...(tags && tags.length > 0 && { tags })
                    });
                }
            });
            setUploadSuccess(`"${title}" uploaded successfully.`);
            setUploadModalOpen(false);
            await fetchVideos();
            setTimeout(() => setUploadSuccess(''), 4000);
        } catch (err) {
            // uploadError is already set by useAzureUpload — no action needed here
            console.error('Error uploading video:', err);
        }
    }, [executeUpload, fetchVideos]);

    const handleUpdate = useCallback(async (id, payload) => {
        try {
            setActionError('');
            await updateVideoContent(id, payload);
            setEditingVideo(null);
            await fetchVideos();
        } catch (err) {
            setActionError(err.message || 'Failed to update video.');
            console.error('Error updating video:', err);
        }
    }, [fetchVideos]);

    const handleDelete = useCallback(async (video) => {
        if (!window.confirm(`Are you sure you want to delete "${video.title}"?`)) return;
        try {
            setActionError('');
            await deleteVideoContent(video.id);
            await fetchVideos();
        } catch (err) {
            setActionError(err.message || 'Failed to delete video.');
            console.error('Error deleting video:', err);
        }
    }, [fetchVideos]);

    return {
        videos,
        loading,
        error,
        searchTerm,
        page,
        totalPages: Math.ceil(totalCount / PAGE_SIZE_VIDEOS),
        playerVideo,
        editingVideo,
        uploadModalOpen,
        uploading: uploadPhase !== null,
        uploadPhase,
        uploadProgress,
        uploadError,
        uploadSuccess,
        actionError,
        handleSearch,
        handlePageChange,
        handleOpenPlayer,
        handleClosePlayer,
        handleOpenEdit,
        handleCloseEdit,
        handleUpload,
        handleUpdate,
        handleDelete,
        handleOpenUploadModal,
        handleCloseUploadModal,
        fetchVideos
    };
}
